import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, X, Music, Users, BarChart3, Paintbrush, Shield, Search, Trash2, Loader as Loader2, RefreshCw, Play, Square, ScrollText } from 'lucide-react';
import { KioskService } from '../services/KioskService';
import { supabase } from '../lib/supabase';
import { BrandingTab, RulesTab, SongsTab, ParticipantsTab, TabBtn, Field, ColorInput, inputCls } from '../components/kiosk/LeagueEditorTabs';

type Tab = 'settings' | 'branding' | 'rules' | 'songs' | 'participants' | 'submissions' | 'admins';

export const AdminKioskLeagueDetail: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as Tab) || 'settings';
  const setTab = (t: Tab) => setSearchParams({ tab: t }, { replace: true });
  const [league, setLeague] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => { if (leagueId) load(); }, [leagueId]);

  const load = async () => {
    if (!leagueId) return;
    try {
      setLoading(true);
      const data = await KioskService.adminGetLeague(leagueId);
      setLeague(data as Record<string, unknown>);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const update = async (patch: Record<string, unknown>) => {
    if (!leagueId) return;
    try {
      setSaving(true);
      setError(null);
      const data = await KioskService.adminUpdateLeague(leagueId, patch);
      setLeague(data as Record<string, unknown>);
      setSuccess('Saved');
      setTimeout(() => setSuccess(null), 1500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-slate-400">Loading...</div>;
  if (!league) return <div className="p-6 text-slate-400">League not found.</div>;

  const l = league as Record<string, string | number | null | string[] | boolean>;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <Link to="/admin/kiosk-leagues" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-4">
        <ArrowLeft className="w-4 h-4" /> All Leagues
      </Link>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">{l.name as string}</h1>
          <div className="text-sm text-slate-400 font-mono">/league/{l.slug as string}</div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`/league/${l.slug}`}
            target="_blank"
            rel="noreferrer"
            className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
          >
            Preview
          </a>
          <select
            value={l.status as string}
            onChange={(e) => update({ status: e.target.value })}
            className="px-3 py-1.5 bg-slate-900 border border-slate-700 text-white rounded-lg text-sm"
          >
            <option value="draft">Draft</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {error && <div className="mb-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">{error}</div>}
      {success && <div className="mb-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-300 text-sm">{success}</div>}

      <div className="border-b border-slate-700 flex gap-1 mb-6">
        <TabBtn active={tab === 'settings'} onClick={() => setTab('settings')} icon={<BarChart3 className="w-4 h-4" />} label="Settings" />
        <TabBtn active={tab === 'branding'} onClick={() => setTab('branding')} icon={<Paintbrush className="w-4 h-4" />} label="Branding" />
        <TabBtn active={tab === 'rules'} onClick={() => setTab('rules')} icon={<ScrollText className="w-4 h-4" />} label="Rules" />
        <TabBtn active={tab === 'songs'} onClick={() => setTab('songs')} icon={<Music className="w-4 h-4" />} label="Songs" />
        <TabBtn active={tab === 'participants'} onClick={() => setTab('participants')} icon={<Users className="w-4 h-4" />} label="Participants" />
        <TabBtn active={tab === 'submissions'} onClick={() => setTab('submissions')} icon={<BarChart3 className="w-4 h-4" />} label="Submissions" />
        <TabBtn active={tab === 'admins'} onClick={() => setTab('admins')} icon={<Shield className="w-4 h-4" />} label="Admins" />
      </div>

      {tab === 'settings' && <SettingsTab league={l} onSave={update} saving={saving} />}
      {tab === 'branding' && <BrandingTab league={l} onSave={update} saving={saving} />}
      {tab === 'rules' && <RulesTab league={l} onSave={update} saving={saving} />}
      {tab === 'songs' && <SongsTab leagueId={leagueId!} />}
      {tab === 'participants' && <ParticipantsTab leagueId={leagueId!} />}
      {tab === 'submissions' && <SubmissionsTab leagueId={leagueId!} leagueSlug={l.slug as string} />}
      {tab === 'admins' && <AdminsTab leagueId={leagueId!} />}
    </div>
  );
};

const SettingsTab: React.FC<{ league: Record<string, any>; onSave: (patch: Record<string, unknown>) => void; saving: boolean }> = ({ league, onSave, saving }) => {
  const [form, setForm] = useState({
    name: league.name || '',
    description: league.description || '',
    signup_mode: league.signup_mode || 'email_verified',
    leaderboard_top_n: league.leaderboard_top_n ?? '',
    require_playback_opt_in: !!league.require_playback_opt_in,
    allow_playback_default: !!league.allow_playback_default,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...form,
      leaderboard_top_n: form.leaderboard_top_n === '' ? null : Number(form.leaderboard_top_n),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-slate-800/60 border border-slate-700 rounded-xl p-5 max-w-2xl">
      <Field label="Name">
        <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
      </Field>
      <Field label="Description">
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} h-24`} />
      </Field>
      <Field label="Sign-up Mode" hint="Email verification is recommended for virtual / public contests. Name-only is best for in-person events.">
        <select value={form.signup_mode} onChange={(e) => setForm({ ...form, signup_mode: e.target.value })} className={inputCls}>
          <option value="email_verified">Email Verified</option>
          <option value="name_only">Name Only (less secure)</option>
        </select>
      </Field>
      <div className="p-3 bg-slate-900/40 border border-slate-700 rounded-lg text-xs text-slate-400">
        Each participant may submit one entry per league. The leaderboard always
        ranks by that single score.
      </div>
      <Field label="Leaderboard Top N" hint="Leave blank to show all.">
        <select
          value={form.leaderboard_top_n.toString()}
          onChange={(e) => setForm({ ...form, leaderboard_top_n: e.target.value })}
          className={inputCls}
        >
          <option value="">All</option>
          <option value="5">Top 5</option>
          <option value="10">Top 10</option>
          <option value="25">Top 25</option>
          <option value="50">Top 50</option>
          <option value="100">Top 100</option>
        </select>
      </Field>
      <label className="flex items-center gap-2 text-slate-300">
        <input type="checkbox" checked={form.allow_playback_default} onChange={(e) => setForm({ ...form, allow_playback_default: e.target.checked })} />
        Default playback opt-in to "allowed" (singers can still disable)
      </label>
      <button disabled={saving} type="submit" className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg disabled:opacity-50">
        <Save className="w-4 h-4" /> Save
      </button>
    </form>
  );
};

const SubmissionsTab: React.FC<{ leagueId: string; leagueSlug: string }> = ({ leagueId, leagueSlug }) => {
  const [subs, setSubs] = useState<Array<Record<string, any>>>([]);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState<Record<string, any> | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [rescoring, setRescoring] = useState<string | null>(null);
  const [mixing, setMixing] = useState<string | null>(null);
  const [toast, setToast] = useState<{ ok: boolean; msg: string } | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [videoModalUrl, setVideoModalUrl] = useState<string | null>(null);
  const vocalAudioRef = useRef<HTMLAudioElement | null>(null);
  const backingAudioRef = useRef<HTMLAudioElement | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (ok: boolean, msg: string) => {
    setToast({ ok, msg });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  };

  const loadSubs = useCallback(async () => {
    const data = await KioskService.adminListSubmissions(leagueId);
    setSubs(data as Array<Record<string, any>>);
    return data;
  }, [leagueId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadSubs();
      setLoading(false);
    })();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (vocalAudioRef.current) { vocalAudioRef.current.pause(); vocalAudioRef.current.src = ''; }
      if (backingAudioRef.current) { backingAudioRef.current.pause(); backingAudioRef.current.src = ''; }
    };
  }, [leagueId, loadSubs]);

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleteError(null);
    setDeleting(true);
    try {
      await KioskService.adminDeleteSubmission(toDelete.id);
      setSubs((prev) => prev.filter((s) => s.id !== toDelete.id));
      setToDelete(null);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete submission');
    } finally {
      setDeleting(false);
    }
  };

  const handleRescore = async (submissionId: string) => {
    setRescoring(submissionId);
    try {
      await KioskService.adminRescoreSubmission(submissionId);
      showToast(true, 'Rescoring started...');

      if (pollRef.current) clearInterval(pollRef.current);
      let attempts = 0;
      const rescoreStartedAt = new Date().toISOString();

      pollRef.current = setInterval(async () => {
        attempts++;
        try {
          const updatedAt = await KioskService.adminGetSubmissionAnalysisUpdatedAt(submissionId);

          if (updatedAt && updatedAt > rescoreStartedAt) {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            const freshData = await KioskService.adminListSubmissions(leagueId);
            setSubs(freshData as Array<Record<string, any>>);
            const freshSub = (freshData as Array<Record<string, any>>).find((s: Record<string, any>) => s.id === submissionId);
            const newScore = freshSub ? Number(freshSub.score || 0).toFixed(1) : '?';
            setRescoring(null);
            showToast(true, `Rescore done: ${newScore}`);
            return;
          }
        } catch { /* ignore polling errors */ }

        if (attempts >= 36) {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          const freshData = await KioskService.adminListSubmissions(leagueId);
          setSubs(freshData as Array<Record<string, any>>);
          setRescoring(null);
          showToast(true, 'Rescore complete');
        }
      }, 5000);
    } catch (e) {
      showToast(false, e instanceof Error ? e.message : 'Rescore failed');
      setRescoring(null);
    }
  };

  const handleMix = async (sub: Record<string, any>) => {
    const recordingId = sub.kiosk_recording?.id || sub.recording_id;
    if (!recordingId) return;
    setMixing(sub.id);
    try {
      await KioskService.triggerMix(recordingId);
      showToast(true, 'Mixing started...');
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const { data } = await supabase
            .from('kiosk_recordings')
            .select('mix_status, mixed_audio_url')
            .eq('id', recordingId)
            .maybeSingle();
          if (data?.mix_status === 'completed') {
            clearInterval(poll);
            setMixing(null);
            showToast(true, 'Mix complete!');
            await loadSubs();
          } else if (data?.mix_status === 'failed') {
            clearInterval(poll);
            setMixing(null);
            showToast(false, 'Mix failed');
          }
        } catch { /* ignore */ }
        if (attempts >= 60) {
          clearInterval(poll);
          setMixing(null);
          showToast(true, 'Mix timeout - check later');
        }
      }, 3000);
    } catch (e) {
      showToast(false, e instanceof Error ? e.message : 'Mix failed');
      setMixing(null);
    }
  };

  const stopPlayback = () => {
    if (vocalAudioRef.current) { vocalAudioRef.current.pause(); vocalAudioRef.current.src = ''; }
    if (backingAudioRef.current) { backingAudioRef.current.pause(); backingAudioRef.current.src = ''; }
    setVideoModalUrl(null);
    setPlayingId(null);
  };

  const handlePlay = (sub: Record<string, any>) => {
    if (playingId === sub.id) {
      stopPlayback();
      return;
    }
    stopPlayback();

    const mixedVideoUrl = sub.kiosk_recording?.mixed_video_url;
    if (mixedVideoUrl) {
      setVideoModalUrl(mixedVideoUrl);
      setPlayingId(sub.id);
      return;
    }

    const vocalsUrl = sub.kiosk_recording?.vocals_preprocessed_url || sub.kiosk_recording?.vocals_only_url;
    const backingUrl = sub.song?.audio_url;
    if (!vocalsUrl) return;

    const vocalsLufs = sub.kiosk_recording?.vocals_lufs;
    const backingLufs = sub.song?.backing_track_lufs != null ? Number(sub.song.backing_track_lufs) : null;

    let backingVolume = 0.3;
    if (vocalsLufs != null && backingLufs != null) {
      const diff = vocalsLufs - backingLufs;
      const attenuationDb = diff - 10;
      backingVolume = Math.max(0.05, Math.min(0.8, Math.pow(10, attenuationDb / 20)));
    }

    const vocal = new Audio(vocalsUrl);
    vocal.volume = 1.0;
    vocalAudioRef.current = vocal;

    if (backingUrl) {
      const backing = new Audio(backingUrl);
      backing.volume = backingVolume;
      backingAudioRef.current = backing;
      vocal.addEventListener('canplay', () => {
        vocal.play();
        backing.currentTime = 0;
        backing.play();
      }, { once: true });
    } else {
      vocal.addEventListener('canplay', () => { vocal.play(); }, { once: true });
    }

    vocal.addEventListener('ended', () => stopPlayback());
    setPlayingId(sub.id);
  };

  if (loading) return <div className="text-slate-400">Loading...</div>;

  return (
    <div className="relative">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium ${toast.ok ? 'bg-green-600/90 text-white' : 'bg-red-600/90 text-white'}`}>
          {toast.msg}
        </div>
      )}

      {videoModalUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={stopPlayback}>
          <div className="relative max-w-3xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={stopPlayback}
              className="absolute -top-10 right-0 p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition"
            >
              <X className="w-5 h-5" />
            </button>
            <video
              src={videoModalUrl}
              autoPlay
              controls
              onEnded={stopPlayback}
              className="w-full rounded-xl shadow-2xl"
            />
          </div>
        </div>
      )}
      <div className="bg-slate-800/60 border border-slate-700 rounded-xl overflow-hidden">
        {subs.length === 0 ? (
        <div className="p-6 text-slate-400 text-center">No submissions yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-900/50 text-slate-400">
            <tr>
              <th className="text-left p-3">Singer</th>
              <th className="text-left p-3">Song</th>
              <th className="text-left p-3">Score</th>
              <th className="text-left p-3">Vocals LUFS</th>
              <th className="text-left p-3">Track LUFS</th>
              <th className="text-left p-3">Offset</th>
              <th className="text-left p-3">Playback</th>
              <th className="text-left p-3">Submitted</th>
              <th className="text-left p-3">Mix</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {subs.map((s) => (
              <tr key={s.id} className="border-t border-slate-700">
                <td className="p-3 text-white">{s.participant?.display_name}</td>
                <td className="p-3 text-slate-300">
                  {s.song?.audio_url ? (
                    <a href={s.song.audio_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-sky-300 transition">
                      {s.song.title || '-'}
                    </a>
                  ) : (s.song?.title || '-')}
                </td>
                <td className="p-3 text-sky-300 font-semibold">
                  {s.kiosk_recording?.vocals_only_url ? (
                    <a href={s.kiosk_recording.vocals_only_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-sky-200 transition">
                      {Number(s.score || 0).toFixed(1)}
                    </a>
                  ) : (
                    Number(s.score || 0).toFixed(1)
                  )}
                </td>
                <td className="p-3 text-slate-300 text-xs font-mono">
                  {s.kiosk_recording?.vocals_lufs != null ? Number(s.kiosk_recording.vocals_lufs).toFixed(1) : '-'}
                </td>
                <td className="p-3 text-slate-300 text-xs font-mono">
                  {s.song?.backing_track_lufs != null ? Number(s.song.backing_track_lufs).toFixed(1) : '-'}
                </td>
                <td className="p-3 text-slate-300 text-xs font-mono">
                  {(() => {
                    const perf = s.performances?.[0];
                    const pa = perf?.performance_analysis?.[0];
                    if (!pa?.time_offset_data) return '-';
                    try {
                      const parsed = typeof pa.time_offset_data === 'string' ? JSON.parse(pa.time_offset_data) : pa.time_offset_data;
                      return parsed.offset_ms != null ? `${Number(parsed.offset_ms).toFixed(0)}ms` : '-';
                    } catch { return '-'; }
                  })()}
                </td>
                <td className="p-3">
                  {(() => {
                    const isVideo = s.kiosk_recording?.recording_type === 'video';
                    const hasVideoReady = !!s.kiosk_recording?.mixed_video_url && s.kiosk_recording?.video_mux_status === 'completed';
                    const isMuxing = s.kiosk_recording?.video_mux_status === 'muxing';
                    const hasAudio = !!(s.kiosk_recording?.vocals_preprocessed_url || s.kiosk_recording?.vocals_only_url);
                    const canPlay = isVideo ? hasVideoReady : hasAudio;

                    if (isVideo && hasVideoReady) {
                      return (
                        <a
                          href={`/league/${leagueSlug}/share/${s.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition bg-white/5 hover:bg-white/10 border border-white/10 text-sky-300"
                          title="Open share page"
                        >
                          <Play className="w-3.5 h-3.5" />
                          Video
                        </a>
                      );
                    }

                    let label = 'N/A';
                    let title = 'No playback available';
                    if (playingId === s.id) {
                      label = 'Stop';
                      title = 'Stop playback';
                    } else if (isVideo && isMuxing) {
                      label = 'Muxing...';
                      title = 'Video is being processed';
                    } else if (isVideo) {
                      label = 'Video';
                      title = 'Video not yet muxed';
                    } else if (hasAudio) {
                      label = 'Audio';
                      title = 'Play audio with backing track';
                    }

                    return (
                      <button
                        onClick={() => handlePlay(s)}
                        disabled={!canPlay || isMuxing}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition ${
                          !canPlay || isMuxing
                            ? 'bg-white/5 border border-white/5 text-slate-600 cursor-not-allowed'
                            : playingId === s.id
                              ? 'bg-green-500/20 border border-green-500/40 text-green-300'
                              : 'bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300'
                        }`}
                        title={title}
                      >
                        {playingId === s.id ? <Square className="w-3.5 h-3.5" /> : isMuxing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                        {label}
                      </button>
                    );
                  })()}
                </td>
                <td className="p-3 text-slate-400 text-xs">{new Date(s.submitted_at).toLocaleString()}</td>
                <td className="p-3">
                  {s.kiosk_recording?.mixed_audio_url ? (
                    <span className="inline-flex items-center gap-1 text-xs whitespace-nowrap">
                      <a href={s.kiosk_recording.mixed_audio_url} target="_blank" rel="noopener noreferrer" className="text-green-400 underline hover:text-green-300 transition">
                        Listen
                      </a>
                      <span className="text-slate-600">/</span>
                      <button
                        onClick={() => handleMix(s)}
                        disabled={mixing === s.id || s.kiosk_recording?.mix_status === 'mixing'}
                        className="text-amber-400 underline hover:text-amber-300 transition disabled:opacity-50"
                      >
                        {mixing === s.id ? 'Mixing...' : 'Remix'}
                      </button>
                    </span>
                  ) : (
                    <button
                      onClick={() => handleMix(s)}
                      disabled={mixing === s.id || s.kiosk_recording?.mix_status === 'mixing'}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 text-xs font-semibold transition disabled:opacity-50 whitespace-nowrap"
                      title="Mix vocals with backing track"
                    >
                      {mixing === s.id || s.kiosk_recording?.mix_status === 'mixing' ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Music className="w-3 h-3" />
                      )}
                      {s.kiosk_recording?.mix_status === 'mixing' ? 'Mixing...' : 'Mix'}
                    </button>
                  )}
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={() => handleRescore(s.id)}
                      disabled={rescoring === s.id}
                      className="p-1.5 rounded-lg bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 text-sky-300 transition disabled:opacity-50"
                      title="Rescore"
                    >
                      {rescoring === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => { setDeleteError(null); setToDelete(s); }}
                      className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 transition"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {toDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-300" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Delete submission?</h3>
                <p className="text-slate-400 text-sm mt-1">
                  This clears the participant's take so they can record again. This cannot be undone.
                </p>
              </div>
            </div>

            <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 text-sm space-y-1 mb-4">
              <div className="flex justify-between"><span className="text-slate-400">Singer</span><span className="text-white font-semibold">{toDelete.participant?.display_name || '-'}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Song</span><span className="text-slate-200">{toDelete.song?.title || '-'}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Score</span><span className="text-sky-300 font-semibold">{Number(toDelete.score || 0).toFixed(1)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Submitted</span><span className="text-slate-300 text-xs">{new Date(toDelete.submitted_at).toLocaleString()}</span></div>
            </div>

            {deleteError && (
              <div className="mb-3 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2.5">
                {deleteError}
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setToDelete(null)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-200 border border-white/10 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white font-semibold disabled:opacity-60"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {deleting ? 'Deleting...' : 'Delete submission'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

const AdminsTab: React.FC<{ leagueId: string }> = ({ leagueId }) => {
  const [admins, setAdmins] = useState<Array<Record<string, any>>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<Array<{ id: string; username: string; full_name: string; email: string }>>([]);

  useEffect(() => { load(); }, [leagueId]);

  const load = async () => {
    try {
      setLoading(true);
      const data = await KioskService.adminListLeagueAdmins(leagueId);
      setAdmins(data as Array<Record<string, any>>);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        setSearching(true);
        const r = await KioskService.adminSearchUsers(q);
        if (!cancelled) setResults(r);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Search failed');
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const add = async (userId: string, role: 'owner' | 'manager') => {
    try {
      setError(null);
      await KioskService.adminAddLeagueAdmin(leagueId, userId, role);
      setQuery('');
      setResults([]);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add admin');
    }
  };

  const remove = async (id: string) => {
    try {
      setError(null);
      await KioskService.adminRemoveLeagueAdmin(id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove admin');
    }
  };

  const existingIds = useMemo(() => new Set(admins.map((a) => a.user_id as string)), [admins]);

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-sky-400" /> League Admins ({admins.length})
        </h3>
        {error && <div className="mb-2 p-2 bg-red-500/10 text-red-300 text-sm rounded">{error}</div>}
        {loading ? (
          <div className="text-slate-400 text-sm">Loading...</div>
        ) : admins.length === 0 ? (
          <p className="text-slate-400 text-sm">No league admins assigned yet. Global admins can still manage this league.</p>
        ) : (
          <ul className="space-y-2 max-h-[500px] overflow-y-auto">
            {admins.map((a) => (
              <li key={a.id as string} className="flex items-center justify-between bg-slate-900/50 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <div className="text-white text-sm truncate">
                    {a.profile?.full_name || a.profile?.username || a.user_id}
                  </div>
                  <div className="text-slate-400 text-xs truncate">
                    {a.profile?.username ? `@${a.profile.username}` : ''}
                    <span className="ml-2 inline-block px-2 py-0.5 rounded bg-slate-800 text-slate-300 uppercase tracking-wide">{a.role}</span>
                  </div>
                </div>
                <button onClick={() => remove(a.id as string)} className="text-red-400 hover:text-red-300 p-1" title="Remove admin">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-3">Add Admin</h3>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search registered users by email, username, or name..."
            className={`${inputCls} pl-10`}
          />
        </div>
        {searching && <div className="text-slate-400 text-xs mb-2">Searching...</div>}
        {query.trim().length >= 2 && !searching && results.length === 0 && (
          <p className="text-slate-500 text-sm">No users match.</p>
        )}
        <ul className="space-y-1 max-h-[500px] overflow-y-auto">
          {results.map((u) => {
            const already = existingIds.has(u.id);
            return (
              <li key={u.id} className="flex items-center justify-between bg-slate-900/50 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <div className="text-white text-sm truncate">{u.full_name || u.username || u.email}</div>
                  <div className="text-slate-400 text-xs truncate">{u.email}{u.username ? ` · @${u.username}` : ''}</div>
                </div>
                {already ? (
                  <span className="text-xs text-slate-500">Already admin</span>
                ) : (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => add(u.id, 'manager')}
                      className="text-xs px-2 py-1 bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 rounded"
                    >
                      Manager
                    </button>
                    <button
                      onClick={() => add(u.id, 'owner')}
                      className="text-xs px-2 py-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded"
                    >
                      Owner
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        <p className="text-xs text-slate-500 mt-3">
          Managers can edit settings, songs, and participants. Owners can additionally add or remove other admins.
        </p>
      </div>
    </div>
  );
};

export default AdminKioskLeagueDetail;
